/**
 * PersonalAnalytics - Tableau de bord des activités personnelles
 * Vue centrée sur les métriques d'un élève individuel
 */
import { useMemo, useCallback, useState } from 'react';
import { Loading } from '@carbon/react';
import { useKeycloak } from '@sphere/auth/data-access';
import {
  usePersonalDashboard,
  usePersonalTopActivities,
  usePersonalRecentAttempts,
  useAnalyticsHealth,
} from '@sphere/analytics/data-access';
import { PersonalAnalyticsDashboard } from '@sphere/analytics/ui';
import { formatAnalyticsPercentOneDecimal } from '@sphere/shared/util';

const PersonalAnalytics: React.FC = () => {
  const { tokenParsed, userInfo, authenticated } = useKeycloak();
  const [selectedPeriod, setSelectedPeriod] = useState('30');

  const sinceDays = useMemo(
    () => Number(selectedPeriod) || 30,
    [selectedPeriod]
  );
  const userId = useMemo(() => {
    // L'analytics identifie l'utilisateur par user_id dans la BDD
    return userInfo?.id || tokenParsed?.sub || '';
  }, [userInfo?.id, tokenParsed?.sub]);

  // Hooks React Query
  const { data: healthData, refetch: refetchHealth } = useAnalyticsHealth();
  const {
    data: dashboardData,
    isLoading: isLoadingDashboard,
    refetch: refetchDashboard,
  } = usePersonalDashboard(userId, sinceDays);
  const { data: topActivitiesData, isLoading: isLoadingTopActivities } =
    usePersonalTopActivities(userId, sinceDays);
  const { data: recentAttemptsData, isLoading: isLoadingRecentAttempts } =
    usePersonalRecentAttempts(userId, sinceDays);

  const isLoading =
    isLoadingDashboard || isLoadingTopActivities || isLoadingRecentAttempts;

  // Aggregate top activities from attempts
  const aggregateTopActivities = useCallback((attempts: Array<any>) => {
    const map: Record<
      string,
      {
        activityId: string;
        activityTitle?: string;
        scores: number[];
        total: number;
        passed: number;
      }
    > = {};
    attempts.forEach((a) => {
      const key = a.activity_id;
      if (!map[key]) {
        map[key] = {
          activityId: key,
          activityTitle: a.activity_title,
          scores: [],
          total: 0,
          passed: 0,
        };
      }
      map[key].total += 1;
      if (typeof a.score_percent === 'number')
        map[key].scores.push(a.score_percent);
      if (a.pass) map[key].passed += 1;
    });
    const list = Object.values(map).map((v) => ({
      activityId: v.activityId,
      activityTitle: v.activityTitle,
      avgScore: v.scores.length
        ? v.scores.reduce((s, x) => s + x, 0) / v.scores.length
        : 0,
      totalAttempts: v.total,
      successRate: v.total ? (v.passed / v.total) * 100 : 0,
    }));
    return list.sort((a, b) => b.avgScore - a.avgScore).slice(0, 5);
  }, []);

  // Compute daily attempts from recent attempts
  const computeDailyAttempts = useCallback((attempts: Array<any>) => {
    const perDay: Record<string, number> = {};
    attempts.forEach((a) => {
      const d = new Date(a.occurred_at).toISOString().slice(0, 10);
      perDay[d] = (perDay[d] || 0) + 1;
    });
    return Object.entries(perDay)
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([date, count]) => ({ date, attempts: count }));
  }, []);

  // Compute insights from dashboard data
  const computeInsights = useCallback(
    (
      summary: any,
      completionRate: number,
      topActivities: Array<{ activityId: string; activityTitle?: string }>
    ) => {
      const insights = [];
      if (summary?.avg_score !== null && summary?.avg_score !== undefined) {
        insights.push({
          type: 'performance',
          title: 'Performance globale',
          description: `Score moyen ${formatAnalyticsPercentOneDecimal(summary.avg_score)}`,
          trend: summary.avg_score >= 70 ? ('positive' as const) : undefined,
          value: summary.avg_score,
        });
      }
      insights.push({
        type: 'engagement',
        title: 'Taux de réussite',
        description: `${formatAnalyticsPercentOneDecimal(completionRate)} d'activités réussies`,
        trend:
          completionRate >= 70
            ? ('positive' as const)
            : completionRate <= 40
              ? ('negative' as const)
              : undefined,
        value: completionRate,
      });
      if (topActivities.length > 0) {
        const top = topActivities[0];
        insights.push({
          type: 'activité',
          title: 'Meilleure activité',
          description: top.activityTitle
            ? `${top.activityTitle} - Voir les détails`
            : 'Voir les détails de votre meilleure activité',
          trend: 'positive' as const,
          activityId: top.activityId,
        });
      }
      return insights;
    },
    []
  );

  const handleRefresh = useCallback(() => {
    refetchHealth();
    refetchDashboard();
  }, [refetchHealth, refetchDashboard]);

  if (isLoading) {
    return <Loading description="Chargement des analytics..." withOverlay />;
  }

  if (!authenticated || !userId || !dashboardData) {
    return null;
  }

  const totalActivities = dashboardData.summary?.total_activities ?? 0;
  const passedActivities = dashboardData.summary?.passed_activities ?? 0;
  const avgScore = dashboardData.summary?.avg_score ?? null;
  const totalAttempts = recentAttemptsData?.attempts?.length ?? 0;
  const completionRate = totalActivities
    ? (passedActivities / totalActivities) * 100
    : 0;

  const topActivities = topActivitiesData?.attempts
    ? aggregateTopActivities(topActivitiesData.attempts)
    : [];

  const recentAttempts = recentAttemptsData?.attempts?.slice(0, 15) ?? [];

  const dailyAttempts = recentAttemptsData?.attempts
    ? computeDailyAttempts(recentAttemptsData.attempts)
    : [];

  const insights = computeInsights(
    dashboardData.summary,
    completionRate,
    topActivities
  );

  const health = healthData
    ? `${healthData.status} · DB: ${healthData.database}`
    : null;

  return (
    <PersonalAnalyticsDashboard
      totalActivities={totalActivities}
      totalAttempts={totalAttempts}
      averageScore={avgScore}
      completionRate={completionRate}
      insights={insights}
      topActivities={topActivities}
      recentAttempts={recentAttempts}
      dailyAttempts={dailyAttempts}
      selectedPeriod={selectedPeriod}
      onPeriodChange={setSelectedPeriod}
      onRefresh={handleRefresh}
      health={health}
      error={null}
      isLoading={isLoading}
    />
  );
};

export default PersonalAnalytics;
